import { For, Show, type JSX } from "solid-js";

import { SEPARATOR_DOT } from "~/util/html/codepoint";

import styles from "../deck.module.css";
import { useDeckWords, type DeckWords } from "../deck_words";
import { DeckImageFigure } from "../deck_image_figure";
import {
  itemRef,
  type ImagesItem,
  type ImagesSlide as ImagesSlideData,
  type PictureSrc,
} from "../types";

/** Under a picture: staff's caption, else what it is ("X-ray · 3/3/2026", or a file's name). */
function itemCaption(item: ImagesItem, words: DeckWords): string {
  if (item.caption) return item.caption;
  if (item.file) return item.file.label;
  const kind = words.t(item.picture.kind === "xray" ? "xRay" : "photo");
  return `${kind} ${SEPARATOR_DOT} ${words.date(item.picture.takenOn)}`;
}

/** Staff's own pictures: one, or two side by side, under the slide's headline, each with its caption, and staff's
 *  words under them. A picture from Imaging is shown as taken, without Pearl's marks. */
export function ImagesSlide(props: {
  slide: ImagesSlideData;
  pictureSrc: PictureSrc;
  failedPictureText?: string;
}): JSX.Element {
  const words = useDeckWords();
  return (
    <div class={styles["imgs"]} data-worded={props.slide.words ? "" : undefined}>
      <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
      <div class={styles["img-row"]} data-count={props.slide.items.length}>
        <For each={props.slide.items}>
          {(item) => (
            <DeckImageFigure
              src={props.pictureSrc(itemRef(item))}
              alt={item.caption ?? (item.picture ?? item.file).label}
              width={(item.picture ?? item.file).width ?? null}
              height={(item.picture ?? item.file).height ?? null}
              failedText={props.failedPictureText ?? words().t("pictureUnavailable")}
              caption={itemCaption(item, words())}
            />
          )}
        </For>
      </div>
      <Show when={props.slide.words}>
        <p class={styles["img-words"]}>{props.slide.words}</p>
      </Show>
    </div>
  );
}
